import type { useIntl } from 'react-intl'
import { isFlightSavedItemData, type FlightSavedItemData, type TransportOption } from 'tripPlanner/api/types'

export type Layover = NonNullable<FlightSavedItemData['layovers']>[number]

export function getFlightSavedData(option: TransportOption): FlightSavedItemData | undefined {
  return isFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
}

export function formatTimeOnly(value: string | undefined): string | undefined {
  if (!value) return undefined
  const match = value.match(/(\d{1,2}):(\d{2})/)
  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : undefined
}

export function getDateParts(value: string | undefined): { year: number; month: number; day: number } | undefined {
  if (!value) return undefined
  const match = value.match(/(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return undefined
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
}

export function getPreferredDateParts(
  date: string | undefined,
  fallbackDateTime: string | undefined,
): { year: number; month: number; day: number } | undefined {
  return getDateParts(date) ?? getDateParts(fallbackDateTime)
}

export function getSavedFlightBoundaryDateParts(
  data: FlightSavedItemData | undefined,
  boundary: 'departure' | 'arrival',
): { year: number; month: number; day: number } | undefined {
  return boundary === 'departure'
    ? getPreferredDateParts(data?.departureDate, data?.departureTime)
    : getPreferredDateParts(data?.arrivalDate, data?.arrivalTime)
}

export function dayOffset(departure: string | undefined, arrival: string | undefined): number {
  const dep = getDateParts(departure)
  const arr = getDateParts(arrival)
  if (!dep || !arr) return 0
  const depMs = Date.UTC(dep.year, dep.month - 1, dep.day)
  const arrMs = Date.UTC(arr.year, arr.month - 1, arr.day)
  return Math.round((arrMs - depMs) / (1000 * 60 * 60 * 24))
}

export function formatTimeRange(departure: string | undefined, arrival: string | undefined): string | undefined {
  const depTime = formatTimeOnly(departure)
  const arrTime = formatTimeOnly(arrival)
  if (!depTime || !arrTime) return undefined
  const offset = dayOffset(departure, arrival)
  return `${depTime} - ${arrTime}${offset > 0 ? `+${offset}` : ''}`
}

export function formatFlightTimeRange(data: FlightSavedItemData | undefined): string | undefined {
  const depTime = formatTimeOnly(data?.departureTime)
  const arrTime = formatTimeOnly(data?.arrivalTime)
  if (!depTime || !arrTime) return undefined
  const offset = dayOffset(data?.departureDate ?? data?.departureTime, data?.arrivalDate ?? data?.arrivalTime)
  return `${depTime} - ${arrTime}${offset > 0 ? `+${offset}` : ''}`
}

export function getLayoverLabel(intl: ReturnType<typeof useIntl>, layover: Layover): string {
  const airport = layover.airportName ?? layover.airportCode ?? ''
  if (layover.duration) {
    return intl.formatMessage({
      id: 'baPnt2qIOf7bv8Q3AMfB',
      defaultMessage: '{duration} layover in {airport}',
      description: 'Layover line with duration shown on an expanded flight leg in trip planner',
    }, { duration: layover.duration, airport })
  }
  return intl.formatMessage({
    id: 'MuXxRJ4PEsqLcdLp6p_G',
    defaultMessage: 'Layover in {airport}',
    description: 'Layover line without a known duration shown on an expanded flight leg in trip planner',
  }, { airport })
}

function dateTimeToMinutes(value: string | undefined): number | undefined {
  if (!value) return undefined
  const match = value.match(/(\d{4})-(\d{2})-(\d{2})[T\s](\d{1,2}):(\d{2})/)
  if (!match) return undefined
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5])) / 60000
}

function formatGapDuration(from: string | undefined, to: string | undefined): string | undefined {
  const start = dateTimeToMinutes(from)
  const end = dateTimeToMinutes(to)
  if (start == null || end == null || end <= start) return undefined
  const total = end - start
  const hours = Math.floor(total / 60)
  const minutes = total % 60
  if (hours === 0) return `${minutes}m`
  if (minutes === 0) return `${hours}h`
  return `${hours}h ${minutes}m`
}

export function getHydratedLeg(
  details: Array<App.FlightOrderItemDetails> | undefined,
  requestedItemIds: Array<string>,
  orderFlightItemId: string | undefined,
  preferredLegIndex: number | undefined,
  fallbackLegIndex: number,
): App.Leg | undefined {
  if (!orderFlightItemId) return undefined
  const requestedIndex = requestedItemIds.indexOf(orderFlightItemId)
  const detail = requestedIndex >= 0 ? details?.[requestedIndex] : undefined
  const legs = detail?.bookingJourney.legs
  if (!legs?.length) return undefined

  return typeof preferredLegIndex === 'number' && legs[preferredLegIndex]
    ? legs[preferredLegIndex]
    : legs[fallbackLegIndex] ?? legs[0]
}

export function getSegmentLayoverLabel(intl: ReturnType<typeof useIntl>, from: App.LegFlight, to: App.LegFlight): string {
  // Prefer the city name ("Singapore") over the airport name ("Changi Intl").
  const location = from.arrivalCity || from.arrivalAirportName || from.arrivalAirport || ''
  const duration = formatGapDuration(from.arrivalDateTime, to.departureDateTime)
    ?? formatGapDuration(`${from.arrivalDate} ${from.arrivalTime}`, `${to.departingDate} ${to.departingTime}`)
  if (duration) {
    return intl.formatMessage({
      id: 'baPnt2qIOf7bv8Q3AMfB',
      defaultMessage: '{duration} layover in {airport}',
      description: 'Layover line with duration shown on an expanded flight leg in trip planner',
    }, { duration, airport: location })
  }
  return intl.formatMessage({
    id: 'MuXxRJ4PEsqLcdLp6p_G',
    defaultMessage: 'Layover in {airport}',
    description: 'Layover line without a known duration shown on an expanded flight leg in trip planner',
  }, { airport: location })
}

// Number of stops on a leg: prefer hydrated segment counts (segments - 1),
// falling back to the saved layover list when the order isn't hydrated.
export function legStops(leg: App.Leg | undefined, fallback: FlightSavedItemData | undefined): number {
  return leg ? Math.max(0, (leg.flights?.length ?? 1) - 1) : (fallback?.layovers?.length ?? 0)
}

export function getStopsLabel(intl: ReturnType<typeof useIntl>, layoverCount: number): string {
  return intl.formatMessage({
    id: 'MXVKOhZhSuI-SR_a3Su8',
    defaultMessage: '{count, plural, =0 {Direct} one {# stop} other {# stops}}',
    description: 'Stop count on the booked flight tile in trip planner',
  }, { count: layoverCount })
}
